import { z } from 'zod';
import { AI_PROVIDERS, profileKeySchema } from './agents.js';
import { erpConnectorDefinitionSchema } from './erp-definition.js';
import { erpConnectorKeySchema } from './erp.js';

/**
 * Tabela de REFERÊNCIA para estimar custo (US$ por 1 milhão de tokens).
 * Os valores reais vêm da fatura de cada fornecedor e devem ser conciliados;
 * revise esta tabela quando os fornecedores alterarem preços.
 */
export const MODEL_PRICES_USD_PER_M: Record<string, { input: number; output: number }> = {
  'gpt-4.1': { input: 2.0, output: 8.0 },
  'gpt-4.1-mini': { input: 0.4, output: 1.6 },
  'gpt-4.1-nano': { input: 0.1, output: 0.4 },
  'gpt-4o-mini': { input: 0.15, output: 0.6 },
  'gemini-2.5-pro': { input: 1.25, output: 10.0 },
  'gemini-2.5-flash': { input: 0.3, output: 2.5 },
  'gemini-2.5-flash-lite': { input: 0.1, output: 0.4 },
  'text-embedding-3-small': { input: 0.02, output: 0 },
  'text-embedding-004': { input: 0, output: 0 },
};

export function estimateCostUsdMicros(model: string, inputTokens: number, outputTokens: number): number {
  const p = MODEL_PRICES_USD_PER_M[model];
  if (!p) return 0;
  return Math.round(inputTokens * p.input + outputTokens * p.output);
}

/** Modelo alternativo usado se o principal falhar (outro fornecedor ou outro modelo). */
export const modelFallbackSchema = z.object({
  aiProvider: z.enum(AI_PROVIDERS),
  model: z.string().trim().min(2).max(80),
});
export type ModelFallback = z.infer<typeof modelFallbackSchema>;

/** Perfil publicado que o Master entrega a cada instância (via agente). */
export const engineProfileSchema = z.object({
  key: profileKeySchema,
  name: z.string(),
  /** Quando encaminhar para este perfil (a Triagem usa para escolher o setor). */
  purpose: z.string().default(''),
  version: z.number().int(),
  aiProvider: z.enum([...AI_PROVIDERS, 'scripted']),
  model: z.string(),
  instructions: z.string(),
  responsePolicy: z.string(),
  handoffRules: z.string(),
  tools: z.array(z.string()),
  temperature: z.number(),
  maxOutputTokens: z.number().int(),
  maxToolCallsPerTurn: z.number().int(),
  /** Ordem de reserva: tentados em sequência quando o anterior falha. */
  fallbacks: z.array(modelFallbackSchema).max(3).default([]),
});
export type EngineProfile = z.infer<typeof engineProfileSchema>;

export const globalKnowledgeArticleSchema = z.object({
  id: z.string(),
  title: z.string(),
  body: z.string(),
  tags: z.array(z.string()),
  version: z.number().int(),
});
export type GlobalKnowledgeArticle = z.infer<typeof globalKnowledgeArticleSchema>;

export const engineConfigSchema = z.object({
  /** Hash do conteúdo — a instância só reprocessa quando muda. */
  version: z.string(),
  generatedAt: z.string(),
  profiles: z.array(engineProfileSchema),
  integration: z.object({
    connector: z.string().nullable(),
    certified: z.boolean(),
    enabledOperations: z.array(z.string()),
    /** Definição da API do ERP (só quando homologado). Sem credenciais. */
    definition: erpConnectorDefinitionSchema.nullable().optional(),
  }),
  budget: z.object({ hardLimit: z.boolean(), exceeded: z.boolean() }),
  globalKnowledge: z.array(globalKnowledgeArticleSchema),
  /** Comportamento do atendimento (valor padrão para configurações de versões anteriores). */
  behavior: z.object({ groupWindowSeconds: z.number().int().min(0).max(20) }).default({ groupWindowSeconds: 4 }),
});
export type EngineConfig = z.infer<typeof engineConfigSchema>;

/** Credencial de IA cifrada no navegador com a chave pública da instância (RSA-OAEP-256 + AES-256-GCM). */
export const sealedCredentialSchema = z.object({
  provider: z.enum(AI_PROVIDERS),
  /** base64: chave AES cifrada com RSA-OAEP */
  wrappedKey: z.string().max(2048),
  /** base64: IV de 12 bytes */
  iv: z.string().max(64),
  /** base64: segredo cifrado com AES-GCM (inclui a tag) */
  ciphertext: z.string().max(4096),
  /** impressão digital da chave pública usada (sha256 hex) */
  keyFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
});
export type SealedCredential = z.infer<typeof sealedCredentialSchema>;

/**
 * Credenciais do ERP de um provedor, cifradas no navegador do Master com a chave pública da
 * instância (mesmo esquema das chaves de IA). O texto aberto é um JSON {baseUrl, fields}.
 */
export const sealedErpCredentialsSchema = z.object({
  connector: erpConnectorKeySchema,
  wrappedKey: z.string().max(2048),
  iv: z.string().max(64),
  ciphertext: z.string().max(16_384),
  keyFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
});
export type SealedErpCredentials = z.infer<typeof sealedErpCredentialsSchema>;

/** Conteúdo (aberto somente na instância) das credenciais do ERP. */
export const erpCredentialPayloadSchema = z.object({
  baseUrl: z
    .string()
    .trim()
    .url('URL inválida')
    .max(300)
    .refine((u) => u.startsWith('https://') || /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/.test(u), 'A URL do ERP precisa usar HTTPS'),
  fields: z.record(z.string().regex(/^[a-z][a-z0-9_]{0,39}$/), z.string().max(1000)),
});
export type ErpCredentialPayload = z.infer<typeof erpCredentialPayloadSchema>;

/** Substituição automática de modelo registrada pela instância (modelo retirado pelo fornecedor). */
export const modelIssueSchema = z.object({
  aiProvider: z.enum(AI_PROVIDERS),
  model: z.string().max(80),
  replacement: z.string().max(80).nullable(),
  reason: z.string().max(200),
  at: z.string(),
});
export type ModelIssue = z.infer<typeof modelIssueSchema>;
